import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Observable } from 'rxjs';
import { RouterLink } from '@angular/router';
import { NgIcon, provideIcons } from '@ng-icons/core';
import {
  lucideDownload,
  lucideLandmark,
  lucideTriangleAlert,
  lucideCircleCheck,
} from '@ng-icons/lucide';
import { HlmAlertImports } from '@spartan-ng/helm/alert';
import { HlmBadgeImports } from '@spartan-ng/helm/badge';
import { HlmButtonImports } from '@spartan-ng/helm/button';
import { HlmCardImports } from '@spartan-ng/helm/card';
import { HlmFieldImports } from '@spartan-ng/helm/field';
import { HlmInputImports } from '@spartan-ng/helm/input';
import { HlmNativeSelectImports } from '@spartan-ng/helm/native-select';
import { HlmSpinnerImports } from '@spartan-ng/helm/spinner';
import { HlmTableImports } from '@spartan-ng/helm/table';
import { HlmTypographyImports } from '@spartan-ng/helm/typography';
import {
  CompanyGroupService,
  Download,
  MonthlyReport,
  SammelueberweisungFormat,
  Settlement,
} from '../../../services/company-group.service';
import { CompanyService } from '../../../services/company.service';
import { formatCurrency } from '../../../utils/currency.utils';
import { berlinPeriod, periodLabel, shiftPeriod } from '../../../utils/period.utils';

const STATUS_LABELS: Record<string, string> = {
  draft: 'Entwurf',
  exported: 'Überweisung heruntergeladen',
  paid: 'Bezahlt',
  cancelled: 'Storniert',
};

/**
 * Monthly overview and settlement with the members.
 *
 * Network only: pick a month, read the overview (PDF/CSV), create or open the month's
 * settlement, download the Sammelüberweisung for its bank, confirm payment. Members see
 * their payouts on /payouts.
 */
@Component({
  selector: 'app-verbund-settlements',
  standalone: true,
  imports: [
    FormsModule,
    RouterLink,
    NgIcon,
    HlmAlertImports,
    HlmBadgeImports,
    HlmButtonImports,
    HlmCardImports,
    HlmFieldImports,
    HlmInputImports,
    HlmNativeSelectImports,
    HlmSpinnerImports,
    HlmTableImports,
    HlmTypographyImports,
  ],
  providers: [
    provideIcons({ lucideDownload, lucideLandmark, lucideTriangleAlert, lucideCircleCheck }),
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'block' },
  templateUrl: './verbund-settlements.component.html',
})
export class VerbundSettlementsComponent {
  private readonly groupService = inject(CompanyGroupService);
  private readonly companyService = inject(CompanyService);

  readonly company = this.companyService.company;
  readonly isNetwork = computed(() => !!this.company()?.is_group_admin);
  readonly networkId = computed(() => this.company()?.public_id ?? null);

  private readonly currentPeriod = berlinPeriod(new Date());
  /** The running month (a preview) and the twelve before it; last month is preselected. */
  readonly periods = Array.from({ length: 13 }, (_, i) => shiftPeriod(this.currentPeriod, -i)).map(
    (p) => ({
      value: p,
      label: p === this.currentPeriod ? `${periodLabel(p)} (laufend)` : periodLabel(p),
    }),
  );
  readonly selectedPeriod = signal(shiftPeriod(this.currentPeriod, -1));
  readonly isRunningMonth = computed(() => this.selectedPeriod() === this.currentPeriod);

  readonly report = signal<MonthlyReport | null>(null);
  readonly settlements = signal<Settlement[]>([]);
  readonly settlement = computed(
    () =>
      this.settlements().find(
        (s) => s.period === this.selectedPeriod() && s.status !== 'cancelled',
      ) ?? null,
  );
  readonly skipped = signal<string[]>([]);

  readonly loading = signal(false);
  readonly busy = signal<string | null>(null);
  readonly error = signal<string | null>(null);

  exportFormat: SammelueberweisungFormat = 'pain.001.001.09';
  executionDate = new Date(Date.now() + 86400000).toISOString().slice(0, 10);

  readonly exportFormats: { value: SammelueberweisungFormat; label: string }[] = [
    { value: 'pain.001.001.09', label: 'SEPA-XML (pain.001.001.09) – für die meisten Banken' },
    { value: 'pain.001.001.03', label: 'SEPA-XML (pain.001.001.03) – ältere Bankprogramme' },
    { value: 'csv', label: 'CSV-Liste (zum manuellen Überweisen)' },
  ];

  constructor() {
    effect(() => {
      const company = this.company();
      if (!company) return;
      if (company.is_group_admin) {
        this.loadNetwork(this.selectedPeriod());
      }
    });
  }

  euro(cents: number | null | undefined): string {
    return `${formatCurrency(cents ?? 0)} €`;
  }

  statusLabel(status: string): string {
    return STATUS_LABELS[status] ?? status;
  }

  selectPeriod(period: string): void {
    this.selectedPeriod.set(period);
    this.skipped.set([]);
    this.loadNetwork(period);
  }

  private loadNetwork(period: string): void {
    const id = this.networkId();
    if (!id) return;
    this.loading.set(true);
    this.error.set(null);
    this.groupService.getMonthlyReport(id, period).subscribe({
      next: (report) => {
        this.report.set(report);
        this.loading.set(false);
      },
      error: (err) => {
        this.loading.set(false);
        this.error.set(err.error?.detail || 'Die Übersicht konnte nicht geladen werden.');
      },
    });
    this.groupService.listSettlements(id).subscribe((rows) => this.settlements.set(rows));
  }

  private replace(updated: Settlement): void {
    this.settlements.update((rows) => [
      updated,
      ...rows.filter((r) => r.public_id !== updated.public_id),
    ]);
  }

  private run<T>(key: string, action: () => Observable<T>, done: (value: T) => void) {
    this.busy.set(key);
    this.error.set(null);
    action().subscribe({
      next: (value) => {
        this.busy.set(null);
        done(value);
      },
      error: async (err) => {
        this.busy.set(null);
        // Blob responses carry the JSON error as a blob.
        const detail =
          err.error instanceof Blob ? JSON.parse(await err.error.text()).detail : err.error?.detail;
        this.error.set(detail || 'Das hat nicht geklappt. Bitte versuche es erneut.');
      },
    });
  }

  private save(download: Download, fallbackName: string): void {
    const url = URL.createObjectURL(download.blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = download.filename !== 'download' ? download.filename : fallbackName;
    link.click();
    URL.revokeObjectURL(url);
  }

  downloadReport(format: 'pdf' | 'csv'): void {
    const id = this.networkId()!;
    const period = this.selectedPeriod();
    this.run(
      `report-${format}`,
      () => this.groupService.downloadMonthlyReport(id, period, format),
      (d) => this.save(d, `schenkwerk-monatsuebersicht-${period}.${format}`),
    );
  }

  createSettlement(): void {
    const id = this.networkId()!;
    this.run(
      'create',
      () => this.groupService.createSettlement(id, this.selectedPeriod()),
      (s) => {
        this.replace(s);
        this.skipped.set(s.skipped_members);
      },
    );
  }

  exportSettlement(settlement: Settlement): void {
    const id = this.networkId()!;
    const ext = this.exportFormat === 'csv' ? 'csv' : 'xml';
    this.run(
      'export',
      () =>
        this.groupService.exportSettlement(
          id,
          settlement.public_id,
          this.exportFormat,
          this.executionDate,
        ),
      (d) => {
        this.save(d, `sammelueberweisung-${settlement.period}.${ext}`);
        this.groupService.listSettlements(id).subscribe((rows) => this.settlements.set(rows));
      },
    );
  }

  markPaid(settlement: Settlement): void {
    const total = this.euro(settlement.total_payout_cents);
    if (
      !confirm(
        `Hast du die Sammelüberweisung über ${total} bei deiner Bank ausgeführt? ` +
          'Deine Mitglieder werden dann per E-Mail benachrichtigt.',
      )
    )
      return;
    this.run(
      'paid',
      () => this.groupService.markSettlementPaid(this.networkId()!, settlement.public_id),
      (s) => this.replace(s),
    );
  }

  cancel(settlement: Settlement): void {
    // Once downloaded, the bank may have the file: cancelling and settling again would pay
    // the same redemptions twice. So that needs an explicit "not handed in".
    const exported = !!settlement.exported_at;
    const question = exported
      ? 'Du hast die Sammelüberweisung für diese Abrechnung bereits heruntergeladen.\n\n' +
        'Storniere nur, wenn du sie NICHT bei deiner Bank eingereicht hast – sonst würden ' +
        'die Einlösungen mit der nächsten Abrechnung ein zweites Mal überwiesen.\n\n' +
        'Die Datei wurde nicht bei der Bank eingereicht: jetzt stornieren?'
      : 'Abrechnung stornieren? Die Einlösungen kommen dann in die nächste Abrechnung.';
    if (!confirm(question)) return;
    this.run(
      'cancel',
      () => this.groupService.cancelSettlement(this.networkId()!, settlement.public_id, exported),
      (s) => this.replace(s),
    );
  }
}
